import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, X } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/ui-bits";

function AreaKnives({ area, onSaved }) {
  const [draft, setDraft] = useState("");
  const saveList = async (knives) => {
    try {
      await api.put(`/knives/areas/${area.location_id}/knives`, { knives });
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (area.knives.some((k) => k.toUpperCase() === v.toUpperCase())) return toast.error(`${v} already exists in ${area.name}`);
    setDraft("");
    saveList([...area.knives, v]);
  };
  const remove = (k) => {
    if (window.confirm(`Remove knife ${k} from ${area.name}?`)) saveList(area.knives.filter((x) => x !== k));
  };
  return (
    <div className="mt-2">
      <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`knife-list-${area.location_id}`}>
        {area.knives.map((k) => (
          <span key={k} className="inline-flex items-center gap-1 rounded-full bg-stone-100 dark:bg-stone-800 border border-border pl-2.5 pr-1 h-8 text-sm font-mono font-semibold">
            {k}
            <button className="h-6 w-6 inline-flex items-center justify-center rounded-full hover:bg-rose-100 text-rose-600" onClick={() => remove(k)} data-testid={`knife-remove-${area.location_id}-${k}`} aria-label={`Remove ${k}`}>
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Knife no. e.g. K1" maxLength={20} className="h-10 font-mono" data-testid={`knife-add-input-${area.location_id}`} />
        <Button variant="outline" className="h-10 gap-1" onClick={add} data-testid={`knife-add-${area.location_id}`}><Plus className="h-4 w-4" /> Add</Button>
      </div>
    </div>
  );
}

/** Admin: numbered knives per area (or a plain expected number for un-numbered areas). */
export default function KnifeExpectedSettings({ areas, onSaved }) {
  const [vals, setVals] = useState({});
  useEffect(() => setVals(Object.fromEntries(areas.map((a) => [a.location_id, String(a.expected)]))), [areas]);
  const plain = areas.filter((a) => !a.knives.length);

  const save = async () => {
    try {
      await api.put("/knives/areas", {
        areas: plain.map((a) => ({ location_id: a.location_id, expected: Math.max(0, parseInt(vals[a.location_id] || "0", 10) || 0) })),
      });
      toast.success("Expected knife numbers saved");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };

  return (
    <Panel title="Knives per area" action={plain.length > 0 && <Button className="h-10 gap-1.5" onClick={save} data-testid="knife-expected-save"><Save className="h-4 w-4" /> Save totals</Button>}>
      <p className="text-xs text-stone-500 mb-4">Add each knife number to its area. Staff then tick every knife at each count. Areas without numbered knives use a total instead.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {areas.map((a) => (
          <div key={a.location_id} className="border border-border rounded-lg p-3" data-testid={`knife-settings-${a.location_id}`}>
            <div className="flex items-center justify-between gap-3">
              <span className="font-semibold text-sm">{a.name}</span>
              {a.knives.length ? (
                <span className="text-xs text-stone-500">{a.knives.length} knife{a.knives.length !== 1 ? "s" : ""}</span>
              ) : (
                <Input type="number" min="0" inputMode="numeric" className="h-10 w-20 text-center tnum" value={vals[a.location_id] ?? ""}
                  onChange={(e) => setVals((v) => ({ ...v, [a.location_id]: e.target.value }))} data-testid={`knife-expected-${a.location_id}`} />
              )}
            </div>
            <AreaKnives area={a} onSaved={onSaved} />
          </div>
        ))}
      </div>
    </Panel>
  );
}
